import { CommonModule } from '@angular/common';
import { ErrorPopupComponent } from '@pc/error-popup';
import { AppValidations, englishNameValidator, InputComponent } from '@pc/input';
import { SeparatorComponent } from '@pc/separator';
import { IconComponent } from '@pc/icon';
import { GradientBtnComponent } from '@pc/gradient-btn';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  Inject,
  inject,
  Injector,
  OnInit,
  signal
} from '@angular/core';
import { BreakpointObserverService } from '@pc/shared';
import { BusinessUnit, DropdownAutocompleteItem, EProcessType, PcAlertsImages, PcButtonIcon } from '@pc/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AutocompleteDropdownComponent } from '@pc/autocomplete-dropdown';
import { AbstractDialogData, AnimationDialogService } from '@pc/dialog-abstract';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AlertPopupComponent } from '../alert-popup/alert-popup.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LeadInsertRequest } from '../../shared/models/lead-insert.interface';
import { LeadInsertService } from '../../shared/services/lead-insert.service';
import { DestinationsStore } from '../../pages/trip-details/where-are-you-travelling/store/destinations.store';
import { ProcessTypeStore } from '../../store/process-type-store/process-type-store';
import { MainStore } from '../../store/main-store/main-store';
import { GetInTouchPopupData, GetInTouchPopupState, PopupStateNotes } from './get-in-touch-popup';
import { Translations, TranslationValue } from '../../shared/services/translations.service';
import { SmartlookService } from '@pc/smartlook';
import { agentPrefix, SmartLookEventEnum } from '../../core/models/smartlook-model';
import { AnalyticsService } from '../../shared/services/analytics.service';
import { catchError, switchMap } from 'rxjs';
import { TravelersStore } from '../../core/stores/travelers.store';

interface ErrorMessage {
  title: string;
  information: string;
}

@Component({
  selector: 'pc-get-in-touch-popup',
  standalone: true,
  imports: [
    CommonModule,
    ErrorPopupComponent,
    InputComponent,
    SeparatorComponent,
    IconComponent,
    GradientBtnComponent,
    TranslateModule,
    ReactiveFormsModule,
    AutocompleteDropdownComponent
  ],
  templateUrl: './get-in-touch-popup.component.html',
  styleUrl: './get-in-touch-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GetInTouchPopupComponent implements OnInit, AfterViewInit {
  public errorMessage = computed(() => this.getErrorMessage());
  public popupState = computed(() => this.data.event?.state || GetInTouchPopupState.generalError);
  public errorTitle = computed(() => this.data.event?.message);
  public countries = computed(() => this.mapCountries());
  public countryCodeValue = signal({ value: '' });
  public isLoading = signal(false);
  public translations = inject(Translations);
  public breakpoint = inject(BreakpointObserverService);
  public injector = inject(Injector);
  public getInTouchPopupLabels = this.translations.translateAndMap('getInTouchPopup', this.mapGetInTouchLabels);
  public form!: FormGroup;
  public arrowIcon = PcButtonIcon.RIGHT_ARROW;
  public phoneIcon = PcButtonIcon.BLUE_PHONE;
  public mobilePopupImage = PcAlertsImages.GetInTouch;
  public desktopPopupImage = PcAlertsImages.Desktop_GetInTouch;
  public BusinessUnit = BusinessUnit;
  public isDesktop = this.breakpoint.getMediumBreakpoint(this.injector);
  public servicePhoneNumber = computed(
    () => this.getInTouchPopupLabels()[this.agentStore.isAgentProcess() ? 'phoneNumberAgent' : 'phoneNumber']
  );
  public noSpaceServicePhoneNumber = computed(() =>
    this.getInTouchPopupLabels()[this.agentStore.isAgentProcess() ? 'phoneNumberAgent' : 'phoneNumber'].replace(
      /\s+/g,
      ''
    )
  );

  private auPrefixId: string | number = '';
  private destroyRef = inject(DestroyRef);
  private dialog = inject(Dialog);
  private dialogRef = inject(DialogRef);
  private animationDialogService = inject(AnimationDialogService);
  private smartlookService = inject(SmartlookService);
  private analytics = inject(AnalyticsService);
  private leadInsertService = inject(LeadInsertService);
  private destinationsStore = inject(DestinationsStore);
  private agentStore = inject(ProcessTypeStore);
  private travelersStore = inject(TravelersStore);
  private mainStore = inject(MainStore);

  constructor(@Inject(DIALOG_DATA) public data: AbstractDialogData<GetInTouchPopupData>) {}

  get firstName(): FormControl {
    return this.form.get('firstName') as FormControl;
  }

  get lastName(): FormControl {
    return this.form.get('lastName') as FormControl;
  }

  get phonePrefix(): FormControl {
    return this.form.get('phonePrefix') as FormControl;
  }

  get phoneNumber(): FormControl {
    return this.form.get('phoneNumber') as FormControl;
  }

  get disableButton(): boolean {
    return this.form.invalid;
  }

  public ngOnInit(): void {
    this.auPrefixId = this.countries().find((country) => country.text.includes('Australia'))?.value ?? '';
    this.initFormGroup();
    this.listenPhoneNumberChanges();
    this.smartlookService.trackEvent(
      agentPrefix(this.mainStore.processType() === EProcessType.AGENTS, SmartLookEventEnum.LEAD_FORM_OPENED)
    );
    this.analytics.pushGeneralEvent('generate_lead', {
      type: this.popupState() === GetInTouchPopupState.ageRestriction ? PopupStateNotes.Over85 : PopupStateNotes.Other
    });
  }

  public ngAfterViewInit(): void {
    this.countryCodeValue.set({ value: this.auPrefixId.toString() });
  }

  public onClose(): void {
    this.animationDialogService.closeDialogWithAnimation(this.dialogRef);
  }

  public onSubmit(): void {
    const isAgeRestriction = this.popupState() === GetInTouchPopupState.ageRestriction;
    this.isLoading.set(true);
    const leadData: LeadInsertRequest = {
      firstName: this.form.value.firstName,
      lastName: this.form.value.lastName,
      phoneCountryId: this.countryCodeValue().value,
      phonePhone: this.form.value.phoneNumber.replace(/\s+/g, ''),
      email: this.form.value.email || '',
      notes: isAgeRestriction ? PopupStateNotes.Over85 : this.form.value.notes || '',
      referredBy: this.mainStore.getReferredById(),
      agentSaleNumberID: this.agentStore.agentSaleNumberDetails()?.id,
      associationId: this.agentStore.organizationId(),
      selectedStateId: this.travelersStore.stateId(),
      siteUserID: this.agentStore.siteUserId() || '',
      leadId: ''
    };

    this.leadInsertService
      .insertLead(leadData)
      .pipe(
        switchMap(() => {
          this.animationDialogService.closeDialogWithAnimation(this.dialogRef, true);

          const dialog = this.animationDialogService.openDialogWithAnimation(this.dialog, AlertPopupComponent, {
            title: this.getInTouchPopupLabels()['submissionSuccessTitle'] || '',
            buttonText: this.getInTouchPopupLabels()['submissionSuccessButton'] || '',
            mobileImgPath: this.mobilePopupImage,
            desktopImgPath: this.desktopPopupImage,
            imgAlt: 'get_in_touch'
          });

          this.smartlookService.trackEvent(
            agentPrefix(this.mainStore.processType() === EProcessType.AGENTS, SmartLookEventEnum.LEAD_CREATED)
          );
          this.analytics.pushGeneralEvent('click_to_call', {
            type: this.popupState() === GetInTouchPopupState.ageRestriction ? 'Over 85' : 'Other'
          });

          return dialog.closed;
        }),
        catchError((error) => {
          this.smartlookService.logError(error);
          this.animationDialogService.closeDialogWithAnimation(this.dialogRef, true);

          const dialog = this.animationDialogService.openDialogWithAnimation(this.dialog, AlertPopupComponent, {
            title: 'Unexpected Error Has Occured',
            mobileImgPath: this.mobilePopupImage,
            desktopImgPath: this.desktopPopupImage,
            imgAlt: 'get_in_touch'
          });

          return dialog.closed;
        })
      )
      .subscribe(() => {
        window.location.reload();
      });
  }

  public onCountryCodeChange(countryCode: string): void {
    this.countryCodeValue.set({ value: countryCode.toString() });
  }

  private initFormGroup(): void {
    this.form = new FormGroup({
      firstName: new FormControl('', englishNameValidator),
      lastName: new FormControl('', englishNameValidator),
      phonePrefix: new FormControl<DropdownAutocompleteItem | undefined>(undefined, Validators.required),
      phoneNumber: new FormControl('', [Validators.required])
    });

    this.phoneNumber.addValidators(this.phoneValidation.bind(this));
  }

  private listenPhoneNumberChanges(): void {
    this.phoneNumber.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value && this.countryCodeValue().value === this.auPrefixId.toString()) {
        const formattedValue = this.formatPhoneNumber(value);
        this.phoneNumber.setValue(formattedValue, { emitEvent: false });
      }
    });
  }

  private formatPhoneNumber(value: string): string {
    const cleaned = value.replace(/\D/g, '');

    let formatted = '';

    const firstGroup = cleaned.slice(0, 4);
    formatted += firstGroup;

    const remaining = cleaned.slice(4);
    for (let i = 0; i < remaining.length; i += 3) {
      formatted += ' ' + remaining.slice(i, i + 3);
    }

    return formatted.trim();
  }

  private mapCountries(): DropdownAutocompleteItem[] {
    return (
      this.destinationsStore.destinations().map((destination) => ({
        text: `(+${destination.phoneCode}) ${destination.title}`,
        value: destination.id,
        image: destination.icon,
        displayIconWithCustomText: `+${destination.phoneCode}`
      })) || []
    );
  }
  private phoneValidation = (control: AbstractControl): ValidationErrors | null => {
    const phoneNumber = control.value.replace(/\s+/g, '');

    if (this.countryCodeValue().value === this.auPrefixId.toString()) {
      return AppValidations.ValidateAuPhone(phoneNumber) === null ? null : { phone: true };
    }

    return AppValidations.ValidateInternationalPhone(phoneNumber) === null ? null : { phone: true };
  };

  private mapGetInTouchLabels(getInTouchPopup: TranslationValue<'getInTouchPopup'>): Record<string, string> {
    return {
      title: getInTouchPopup.title.generalError,
      titleAge: getInTouchPopup.title.ageRestriction,
      information: getInTouchPopup.information.generalError,
      informationAge: getInTouchPopup.information.ageRestriction,
      imgAlt: getInTouchPopup.imgAlt,
      phoneNumber: getInTouchPopup.phoneNumber,
      phoneNumberAgent: getInTouchPopup.phoneNumberAgent,
      submitButton: getInTouchPopup.submitButton,
      formFirstName: getInTouchPopup.form.firstName,
      formLastName: getInTouchPopup.form.lastName,
      formCallingCode: getInTouchPopup.form.callingCode,
      formPhoneNumber: getInTouchPopup.form.phoneNumber,
      formPhonePlaceholder: getInTouchPopup.form.phonePlaceholder,
      formNoResult: getInTouchPopup.form.noResult,
      submissionSuccessTitle: getInTouchPopup.success.title,
      submissionSuccessButton: getInTouchPopup.success.button
    };
  }

  private getErrorMessage(): ErrorMessage {
    switch (this.popupState()) {
      case GetInTouchPopupState.ageRestriction:
        return {
          title: this.getInTouchPopupLabels()['titleAge'],
          information: this.getInTouchPopupLabels()['informationAge']
        };
      default:
        return {
          title: this.errorTitle() || this.getInTouchPopupLabels()['title'],
          information: this.getInTouchPopupLabels()['information']
        };
    }
  }
}
